import { Box, Typography } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import type { CustomerEvidenceReference } from "../../contexts/chat/nucleus_conversation_client";
import type { Message } from "../../contexts/chat/type";
import { CHAT_FONT_STACK } from "../../contexts/material_ui/theme";
import {
  customerFacingEvidenceTitle,
  customerFacingEvidenceText,
  evidenceFallback,
} from "./customer_facing";

type VerificationTone = "success" | "warning" | "neutral";

const statusCopy = (
  grounding: NonNullable<Message["nucleusGrounding"]>,
  sources: CustomerEvidenceReference[],
): { label: string; detail: string; tone: VerificationTone } => {
  const evidenceKinds = new Set(sources.map((source) => source.kind));
  const hasDocumentation = evidenceKinds.has("documentation");
  const hasRunEvidence =
    evidenceKinds.has("run_data") || evidenceKinds.has("lineage");

  if (
    grounding.status === "unsupported" ||
    grounding.reconciliationStatus === "unsupported"
  ) {
    return {
      label: "Not verified",
      detail: "The available evidence did not support this answer.",
      tone: "neutral",
    };
  }
  if (hasDocumentation && !hasRunEvidence) {
    const sourceCount = sources.length;
    return {
      label: "Based on customer documentation",
      detail: `${sourceCount} customer documentation source${sourceCount === 1 ? "" : "s"} used; this does not verify live run values.`,
      tone: "neutral",
    };
  }
  if (
    hasRunEvidence &&
    grounding.status === "full_trace" &&
    grounding.reconciliationStatus === "matched"
  ) {
    return {
      label: "Calculation reconciled",
      detail: "The supplied components match the value shown for this run.",
      tone: "success",
    };
  }
  if (hasRunEvidence) {
    if (grounding.reconciliationStatus === "matched") {
      return {
        label: "Saved value matched",
        detail:
          "The saved value matches; deeper contributor or source detail may be unavailable.",
        tone: "warning",
      };
    }
    return {
      label: "Run evidence used",
      detail: "Some calculation detail was not available from this view.",
      tone: "warning",
    };
  }
  return {
    label: "Evidence available",
    detail: "Open the supporting evidence for the basis of this answer.",
    tone: "warning",
  };
};

const toneStyles: Record<
  VerificationTone,
  { background: string; border: string; color: string }
> = {
  success: {
    background: "rgba(40, 122, 75, 0.08)",
    border: "rgba(40, 122, 75, 0.2)",
    color: "#287A4B",
  },
  warning: {
    background: "rgba(154, 103, 0, 0.075)",
    border: "rgba(154, 103, 0, 0.2)",
    color: "#8A5C00",
  },
  neutral: {
    background: "rgba(92, 96, 102, 0.065)",
    border: "rgba(92, 96, 102, 0.16)",
    color: "#5C6066",
  },
};

const safeSource = (source: CustomerEvidenceReference) => {
  const fallback = evidenceFallback(source.kind);
  return {
    kind: source.kind,
    title: customerFacingEvidenceTitle(source.title, fallback, 160),
    summary: customerFacingEvidenceText(source.summary, "", 320),
    document:
      source.kind === "documentation" && source.document
        ? {
            title: customerFacingEvidenceTitle(
              source.document.title,
              "Customer documentation",
              160,
            ),
            section: customerFacingEvidenceTitle(
              source.document.section,
              "",
              160,
            ),
            excerpt: customerFacingEvidenceText(
              source.document.excerpt,
              "",
              500,
            ),
            freshness: customerFacingEvidenceText(
              source.document.freshness,
              "",
              120,
            ),
          }
        : undefined,
  };
};

const sourceVerificationCopy = (
  source: ReturnType<typeof safeSource>,
  grounding: NonNullable<Message["nucleusGrounding"]>,
): string => {
  if (source.summary || source.document) return "";
  if (source.kind === "documentation") {
    return "This source supports the answer with customer documentation.";
  }
  if (grounding.reconciliationStatus === "matched") {
    return grounding.status === "full_trace"
      ? "Verified against the selected run with calculation and source detail available."
      : "Verified against the displayed value in the selected run; some source detail is still unavailable.";
  }
  if (
    grounding.status === "unsupported" ||
    grounding.reconciliationStatus === "unsupported"
  ) {
    return "This source did not verify the answer.";
  }
  return "This source supports part of the answer from the selected run.";
};

const sourceDetailScore = (source: ReturnType<typeof safeSource>): number =>
  source.summary.length +
  (source.document
    ? source.document.title.length +
      source.document.section.length +
      source.document.excerpt.length +
      source.document.freshness.length
    : 0);

const groupedSources = (
  sources: ReturnType<typeof safeSource>[],
): ReturnType<typeof safeSource>[] => {
  const grouped = new Map<string, ReturnType<typeof safeSource>>();
  for (const source of sources) {
    const key = `${source.kind}:${source.title.toLocaleLowerCase("en-US")}`;
    const existing = grouped.get(key);
    if (!existing || sourceDetailScore(source) > sourceDetailScore(existing)) {
      grouped.set(key, source);
    }
  }
  return Array.from(grouped.values());
};

export const MessageGrounding = ({
  grounding,
  sources = [],
}: {
  grounding: NonNullable<Message["nucleusGrounding"]>;
  sources?: CustomerEvidenceReference[];
}) => {
  if (sources.length === 0) return null;
  const status = statusCopy(grounding, sources);
  const tone = toneStyles[status.tone];
  const Icon =
    status.tone === "success"
      ? CheckCircleRoundedIcon
      : status.tone === "warning"
        ? InfoOutlinedIcon
        : ErrorOutlineRoundedIcon;
  const safeSources = sources.map(safeSource);
  const evidenceKinds = new Set(sources.map((source) => source.kind));
  const documentationOnly =
    evidenceKinds.has("documentation") &&
    !evidenceKinds.has("run_data") &&
    !evidenceKinds.has("lineage");
  const hasRunEvidence =
    evidenceKinds.has("run_data") || evidenceKinds.has("lineage");
  // The answer body owns run and lineage detail. Keep those summaries in
  // conversation state for follow-ups, but do not duplicate them inside a
  // nested evidence card. Documentation remains independently inspectable.
  const displaySources = hasRunEvidence
    ? safeSources.filter((source) => source.kind === "documentation")
    : safeSources;
  const visibleSources = groupedSources(displaySources);
  const fallbackLabels = hasRunEvidence
    ? []
    : Array.from(
        new Set(
          grounding.evidenceLabels.map((label) =>
            customerFacingEvidenceTitle(label, "Supporting evidence", 160),
          ),
        ),
      );
  const sourceCountCopy = `${
    status.label === "Not verified" ? "Checked" : "Built from"
  } ${sources.length} source${sources.length === 1 ? "" : "s"}`;
  return (
    <Box
      component="details"
      aria-label="Answer verification"
      sx={{
        mt: 1.1,
        borderTop: "1px solid",
        borderColor: "rgba(102, 116, 138, 0.18)",
        fontFamily: CHAT_FONT_STACK,
        "&[open]": { pb: 0.75 },
        "& .nucleus-receipt-hide": { display: "none" },
        "&[open] .nucleus-receipt-show": { display: "none" },
        "&[open] .nucleus-receipt-hide": { display: "inline" },
      }}
    >
      <Box
        component="summary"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.65,
          minHeight: 32,
          py: 0.55,
          cursor: "pointer",
          listStyle: "none",
          "&::-webkit-details-marker": { display: "none" },
          "&:focus-visible": {
            outline: "2px solid #3877F7",
            outlineOffset: 2,
            borderRadius: 1,
          },
        }}
      >
        <Icon aria-hidden="true" sx={{ fontSize: 15, color: tone.color }} />
        <Box
          sx={{
            minWidth: 0,
            flex: 1,
            display: "flex",
            alignItems: "baseline",
            gap: 0.45,
            flexWrap: "wrap",
          }}
        >
          <Typography
            component="span"
            sx={{
              fontFamily: CHAT_FONT_STACK,
              fontSize: "0.73rem",
              lineHeight: 1.35,
              fontWeight: 700,
              color: tone.color,
            }}
          >
            {status.label}
          </Typography>
          <Typography
            component="span"
            sx={{
              fontFamily: CHAT_FONT_STACK,
              fontSize: "0.7rem",
              lineHeight: 1.35,
              color: "text.secondary",
            }}
          >
            {sourceCountCopy}
          </Typography>
        </Box>
        <Typography
          component="span"
          className="nucleus-receipt-show"
          sx={{
            flexShrink: 0,
            fontFamily: CHAT_FONT_STACK,
            fontSize: "0.7rem",
            lineHeight: 1.35,
            fontWeight: 700,
            color: "#2C56B0",
          }}
        >
          see how
        </Typography>
        <Typography
          component="span"
          className="nucleus-receipt-hide"
          sx={{
            flexShrink: 0,
            fontFamily: CHAT_FONT_STACK,
            fontSize: "0.7rem",
            lineHeight: 1.35,
            fontWeight: 700,
            color: "#2C56B0",
          }}
        >
          hide
        </Typography>
      </Box>

      <Typography
        sx={{
          px: 0.25,
          pt: 0.35,
          fontFamily: CHAT_FONT_STACK,
          fontSize: "0.75rem",
          lineHeight: 1.5,
          color: "text.secondary",
        }}
      >
        {status.detail}
      </Typography>

      {(displaySources.length > 0 || fallbackLabels.length > 0) && (
        <Box sx={{ mt: 0.85, px: 0.25 }}>
          <Typography
            component="div"
            sx={{
              fontFamily: CHAT_FONT_STACK,
              fontSize: "0.74rem",
              lineHeight: 1.4,
              fontWeight: 700,
              color: "text.secondary",
            }}
          >
            {documentationOnly ? "Sources" : "Supporting evidence"} (
            {displaySources.length || fallbackLabels.length})
          </Typography>
          {displaySources.length > visibleSources.length && (
            <Typography
              sx={{
                mt: 0.65,
                fontFamily: CHAT_FONT_STACK,
                fontSize: "0.72rem",
                lineHeight: 1.4,
                color: "text.secondary",
              }}
            >
              {documentationOnly
                ? `${displaySources.length} matches across ${visibleSources.length} document${visibleSources.length === 1 ? "" : "s"}.`
                : `${displaySources.length} sources grouped into ${visibleSources.length} evidence type${visibleSources.length === 1 ? "" : "s"}.`}
            </Typography>
          )}
          <Box sx={{ display: "grid", gap: 0.7, mt: 0.75 }}>
            {displaySources.length > 0
              ? visibleSources.map((source, index) => {
                  const detail =
                    source.summary || sourceVerificationCopy(source, grounding);
                  const documentHeading = source.document
                    ? [
                        source.document.title.localeCompare(
                          source.title,
                          "en-US",
                          { sensitivity: "base" },
                        ) === 0
                          ? ""
                          : source.document.title,
                        source.document.section,
                      ]
                        .filter(Boolean)
                        .join(" · ")
                    : "";
                  return (
                    <Box
                      key={`${source.title}:${index}`}
                      sx={{
                        p: 0.9,
                        borderRadius: 1.5,
                        bgcolor: "rgba(255, 255, 255, 0.78)",
                        border: "1px solid rgba(16, 17, 19, 0.055)",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: CHAT_FONT_STACK,
                          fontSize: "0.76rem",
                          lineHeight: 1.4,
                          fontWeight: 700,
                        }}
                      >
                        {source.title}
                      </Typography>
                      {detail && (
                        <Typography
                          sx={{
                            display: "block",
                            mt: 0.25,
                            fontFamily: CHAT_FONT_STACK,
                            fontSize: "0.72rem",
                            lineHeight: 1.45,
                            color: "text.secondary",
                          }}
                        >
                          {detail}
                        </Typography>
                      )}
                      {source.document && (
                        <Box sx={{ mt: 0.5 }}>
                          {documentHeading && (
                            <Typography
                              sx={{
                                fontFamily: CHAT_FONT_STACK,
                                fontSize: "0.72rem",
                                lineHeight: 1.4,
                                fontWeight: 700,
                                color: "text.secondary",
                              }}
                            >
                              {documentHeading}
                            </Typography>
                          )}
                          {source.document.excerpt && (
                            <Typography
                              sx={{
                                mt: 0.25,
                                fontFamily: CHAT_FONT_STACK,
                                fontSize: "0.72rem",
                                lineHeight: 1.45,
                                color: "text.secondary",
                              }}
                            >
                              {source.document.excerpt}
                            </Typography>
                          )}
                          {source.document.freshness && (
                            <Typography
                              sx={{
                                mt: 0.25,
                                fontFamily: CHAT_FONT_STACK,
                                fontSize: "0.7rem",
                                lineHeight: 1.4,
                                color: "text.secondary",
                              }}
                            >
                              {source.document.freshness}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })
              : fallbackLabels.map((label) => (
                  <Typography
                    key={label}
                    sx={{
                      fontFamily: CHAT_FONT_STACK,
                      fontSize: "0.72rem",
                      color: "text.secondary",
                    }}
                  >
                    {label}
                  </Typography>
                ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};
